import { useAnalytics } from '@/hooks/useAnalytics';
import { useUrl } from '@/hooks/useUrl';
import { AnalyticsPage, AppLog, Channel, DatabaseLog, EnvInfo, SessionData } from '@/types/analytics';
import { Box } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import AnalyticsHeader from './AnalyticsHeader';
import AppLogs from './appLogs/AppLogs';
import DatabaseLogs from './databaseLogs/DatabaseLogs';
import PromptsLogs from './promptsLogs/PromptsLogs';
import SessionLogs from './sessionLogs/SessionLogs';

type AnalyticsLogsProps = {
  uuid: string;
  selectedPage: AnalyticsPage;
  setSelectedPage: (newPage: AnalyticsPage) => void;
};

const AnalyticsLogs: FC<AnalyticsLogsProps> = ({ uuid, selectedPage, setSelectedPage }) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { getDbLogs, getAppLogs, getSessionLogs, getCall } = useAnalytics();
  const { setSavedUrl } = useUrl();

  const [envInfo, setEnvInfo] = useState<EnvInfo>();
  const [channels, setChannels] = useState<Channel[]>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [sessionData, setSessionData] = useState<SessionData>();
  const [appLogs, setAppLogs] = useState<AppLog[]>([]);
  const [dbLogs, setDbLogs] = useState<DatabaseLog[]>([]);

  const getExportName = (type: string, envInfo?: EnvInfo) => {
    if (!envInfo) return type;

    return `${envInfo.environment}_${type}_${envInfo.uuid}_${envInfo.hostname}_${envInfo.starttime || ''}`;
  };

  const navigateToBuilder = (blockId: string) => {
    setSavedUrl(window.document.location.search);
    navigate(`/builder?appname=${envInfo?.appname || ''}&blockid=${blockId}`);
  };

  useEffect(() => {
    if (!auth.isAuthenticated) return;
    switch (selectedPage) {
      case 'Session Flow':
      case 'Prompts Log':
        if (!sessionData) {
          setIsLoading(true);
          getSessionLogs.mutate(uuid, {
            onSuccess: (sessionLogs) => {
              if (!sessionLogs) return;
              const { meta, ...data } = sessionLogs;
              setEnvInfo(meta);
              setSessionData(data);
            },
            onSettled: () => setIsLoading(false)
          });
        }
        break;
      case 'App Log':
        if (!appLogs.length) {
          setIsLoading(true);
          getAppLogs.mutate(uuid, {
            onSuccess: (appLogs) => {
              if (!appLogs) return;
              const { log, ...info } = appLogs;
              !envInfo && setEnvInfo(info);
              setAppLogs(log || []);
            },
            onSettled: () => setIsLoading(false)
          });
        }
        break;
      case 'Database Log':
        if (!dbLogs.length) {
          setIsLoading(true);

          getDbLogs.mutate(uuid, {
            onSuccess: (dbLogs) => {
              if (!dbLogs) return;
              const { log, ...info } = dbLogs;
              !envInfo && setEnvInfo(info);
              setDbLogs(log || []);
            },
            onSettled: () => setIsLoading(false)
          });
        }
        break;
    }
  }, [auth, selectedPage]);

  useEffect(() => {
    auth.isAuthenticated && getCall.mutate(uuid, { onSuccess: (data) => setChannels(data?.channels) });
  }, [auth]);

  return (
    <>
      <AnalyticsHeader
        envInfo={envInfo}
        channels={channels || []}
        selectedPage={selectedPage}
        onSelect={(newPage) => setSelectedPage(newPage)}
      />
      <Box height='inherit' display={selectedPage === 'Session Flow' ? 'inherit' : 'none'}>
        <SessionLogs
          name={getExportName('session', envInfo)}
          uuid={uuid}
          sessionData={sessionData}
          navigateToBuilder={navigateToBuilder}
          isLoading={isLoading}
        />
      </Box>
      <Box height='inherit' display={selectedPage === 'App Log' ? 'inherit' : 'none'}>
        <AppLogs
          name={getExportName('app', envInfo)}
          uuid={uuid}
          envInfo={envInfo}
          appLogs={appLogs}
          selected={selectedPage === 'App Log'}
          isLoading={isLoading}
        />
      </Box>
      <Box height='inherit' display={selectedPage === 'Database Log' ? 'inherit' : 'none'}>
        <DatabaseLogs
          name={getExportName('db', envInfo)}
          dbLogs={dbLogs}
          selected={selectedPage === 'Database Log'}
          isLoading={isLoading}
        />
      </Box>
      <Box height='inherit' display={selectedPage === 'Prompts Log' ? 'inherit' : 'none'}>
        <PromptsLogs sessionData={sessionData} isLoading={isLoading} />
      </Box>
    </>
  );
};

export default AnalyticsLogs;
